import { c as _c } from "react/compiler-runtime";
import { BackToHomeButton } from "@/components/BackToHomeButton";
import { Button } from "@/components/Button";
import { Card } from "@/components/Card";
import { HighlightedText, type TextSegment } from "@/components/HighlightedText";
import { SpinningLogo } from "@/components/SpinningLogo";
import { useKonamiCode } from "@/hooks/useKonamiCode";
import { useScreenSizeGuard } from "@/hooks/useScreenSizeGuard";
import { cn, getCSSVariable } from "@/utils/helpers";
import { Lightbulb, MonitorCog } from "lucide-react";
import { useState } from "react";
const secretContent = {
  welcomeMessage: "You have excellent detective skills! 🕵️",
  factLabel: "Fun Fact",
  facts: ["This portfolio was built with Next.js, TypeScript, and lots of ☕", "The spinning logo uses 3D transforms and CSS animations", "Every color in this theme is carefully chosen from CSS variables", "The console ASCII art is dynamically generated from theme colors", "You're one of the few people who found this page!"],
  legendStatus: "LEGEND STATUS!",
  description: [{
    text: "You discovered the hidden "
  }, {
    text: "easter egg",
    highlight: true
  }, {
    text: "! As a reward, here's a random fact about this "
  }, {
    text: "portfolio",
    highlight: true
  }, {
    text: ":"
  }] as TextSegment[],
  buttonText: "Console Secret",
  statusMessages: {
    clickProgress: (clicks: number) => `Click the logo to reveal more secrets (${clicks}/5 clicks)`,
    konamiActive: "🎮 Konami Code is now active! Try it out...",
    allRevealed: "All secrets revealed!"
  },
  ariaLabels: {
    logo: "Interactive logo - click to reveal secrets",
    factCard: "Random portfolio fact",
    statusRegion: "Secret page status updates"
  },
  consoleMessages: {
    konamiActivated: "🎮 KONAMI CODE ACTIVATED!",
    extraSecret: "🎉 Extra Secret: Try the Konami Code: ↑ ↓ ↑ ↓ ← → ← → ↓ ↑"
  },
  audioWarning: "Audio playback blocked by browser"
};
const CLICKS_TO_UNLOCK = 5;
const EMOJI_CLASSES = "hidden sm:inline";
const getStatusMessage = (clicks: number, konamiEnabled: boolean, konamiActivated: boolean) => {
  if (clicks < CLICKS_TO_UNLOCK) {
    return secretContent.statusMessages.clickProgress(clicks);
  }
  if (konamiEnabled && !konamiActivated) {
    return secretContent.statusMessages.konamiActive;
  }
  return secretContent.statusMessages.allRevealed;
};
const logKonamiActivation = () => {
  const pink = getCSSVariable("--button-primary-glow-1");
  console.log(`%c${secretContent.consoleMessages.konamiActivated}`, `color: ${pink}; font-size: 1.5rem; font-weight: bold; text-shadow: 0 0 10px ${pink};`);
  try {
    const audio = new Audio("/sounds/konami.mp3");
    audio.volume = 0.5;
    audio.play().catch(() => console.warn(secretContent.audioWarning));
  } catch (e) {
    console.error(e);
  }
};
const logConsoleSecret = () => {
  const purple = getCSSVariable("--color-accent-3");
  console.log(`%c${secretContent.consoleMessages.extraSecret}`, `color: ${purple}; font-size: 1rem; font-weight: bold;`);
};
export default function SecretPage() {
  const $ = _c(40);
  useScreenSizeGuard();
  const [clicks, setClicks] = useState(0);
  const [konamiEnabled, setKonamiEnabled] = useState(false);
  const [consoleSecretClicked, setConsoleSecretClicked] = useState(false);
  const konamiActivated = useKonamiCode(konamiEnabled, logKonamiActivation);
  const currentFact = secretContent.facts[clicks % secretContent.facts.length];
  const isLogoClickable = clicks < CLICKS_TO_UNLOCK;
  let t0;
  if ($[0] !== clicks || $[1] !== isLogoClickable) {
    t0 = () => {
      if (!isLogoClickable) {
        return;
      }
      const newClicks = clicks + 1;
      setClicks(newClicks);
      if (newClicks === CLICKS_TO_UNLOCK) {
        setKonamiEnabled(true);
      }
    };
    $[0] = clicks;
    $[1] = isLogoClickable;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const handleLogoClick = t0;
  let t1;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      setConsoleSecretClicked(true);
      logConsoleSecret();
    };
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const handleConsoleSecretClick = t1;
  const t2 = !isLogoClickable;
  const t3 = isLogoClickable ? "cursor-pointer hover:scale-110" : "cursor-default";
  let t4;
  if ($[4] !== t3) {
    t4 = cn("transition-transform mt-5 duration-300", t3);
    $[4] = t3;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <SpinningLogo />;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== handleLogoClick || $[8] !== t2 || $[9] !== t4) {
    t6 = <button onClick={handleLogoClick} disabled={t2} aria-label={secretContent.ariaLabels.logo} className={t4}>{t5}</button>;
    $[7] = handleLogoClick;
    $[8] = t2;
    $[9] = t4;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  const t7 = konamiActivated && "animate-bounce";
  let t8;
  if ($[11] !== t7) {
    t8 = cn("heading-1 text-glow", t7);
    $[11] = t7;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  let t9;
  if ($[13] !== konamiActivated) {
    t9 = konamiActivated ? <><span className={EMOJI_CLASSES} aria-hidden="true">🎮{" "}</span>{secretContent.legendStatus}<span className={EMOJI_CLASSES} aria-hidden="true">{" "}🎮</span></> : secretContent.welcomeMessage;
    $[13] = konamiActivated;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  let t10;
  if ($[15] !== t8 || $[16] !== t9) {
    t10 = <h1 className={t8}>{t9}</h1>;
    $[15] = t8;
    $[16] = t9;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  let t11;
  if ($[18] !== clicks || $[19] !== currentFact || $[20] !== konamiActivated) {
    t11 = !konamiActivated && <><p className="body-large opacity-90"><HighlightedText segments={secretContent.description} /></p><Card hover={false} className="w-full p-6 flex flex-col items-start text-left" aria-label={secretContent.ariaLabels.factCard}><div className="flex items-center gap-2 mb-6"><Lightbulb className="w-5 h-5 text-(--color-accent-3)" aria-hidden="true" /><span className="heading-6 text-glow">{secretContent.factLabel}</span></div><p key={clicks} className="body-base animate-fade-in">{currentFact}</p></Card></>;
    $[18] = clicks;
    $[19] = currentFact;
    $[20] = konamiActivated;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  let t12;
  if ($[22] !== clicks || $[23] !== konamiActivated || $[24] !== konamiEnabled) {
    t12 = getStatusMessage(clicks, konamiEnabled, konamiActivated);
    $[22] = clicks;
    $[23] = konamiActivated;
    $[24] = konamiEnabled;
    $[25] = t12;
  } else {
    t12 = $[25];
  }
  let t13;
  if ($[26] !== t12) {
    t13 = <p className="body-small" role="status" aria-live="polite" aria-label={secretContent.ariaLabels.statusRegion}>{t12}</p>;
    $[26] = t12;
    $[27] = t13;
  } else {
    t13 = $[27];
  }
  let t14;
  if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <BackToHomeButton />;
    $[28] = t14;
  } else {
    t14 = $[28];
  }
  let t15;
  if ($[29] !== consoleSecretClicked || $[30] !== konamiEnabled) {
    t15 = konamiEnabled && <Button variant="secondary" iconPosition="right" icon={<MonitorCog />} onClick={handleConsoleSecretClick} disabled={consoleSecretClicked}>{secretContent.buttonText}</Button>;
    $[29] = consoleSecretClicked;
    $[30] = konamiEnabled;
    $[31] = t15;
  } else {
    t15 = $[31];
  }
  let t16;
  if ($[32] !== t15) {
    t16 = <nav className="w-full flex flex-wrap justify-center gap-4 mt-4" aria-label="Navigation">{t14}{t15}</nav>;
    $[32] = t15;
    $[33] = t16;
  } else {
    t16 = $[33];
  }
  let t17;
  if ($[34] !== t10 || $[35] !== t11 || $[36] !== t13 || $[37] !== t16 || $[38] !== t6) {
    t17 = <section className="min-h-screen flex flex-col items-center justify-center p-8 pt-28 gap-8"><article className="flex flex-col items-center gap-6 max-w-2xl text-center">{t6}{t10}{t11}{t13}{t16}</article></section>;
    $[34] = t10;
    $[35] = t11;
    $[36] = t13;
    $[37] = t16;
    $[38] = t6;
    $[39] = t17;
  } else {
    t17 = $[39];
  }
  return t17;
}